import type { ReactNode } from "react";
import { useFileObjectUrl } from "@/lib/useFileObjectUrl";

const fileInputClass =
  "w-full text-sm file:mr-3 file:rounded file:border-0 file:bg-brand-blue/10 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-brand-blue";

type AdminImagePickerProps = {
  label: string;
  hint?: ReactNode;
  inputId: string;
  /** Current image URL from the database (not the file picker). */
  remoteUrl: string | null | undefined;
  selectedFile: File | null;
  onFileChange: (file: File | null) => void;
  /** Clear saved URL and any selected file (caller updates state). */
  onClear: () => void;
  disabled?: boolean;
  /** Classes for the preview `<img>` (default: landscape thumbnail). */
  imgClassName?: string;
  accept?: string;
};

export function AdminImagePicker({
  label,
  hint,
  inputId,
  remoteUrl,
  selectedFile,
  onFileChange,
  onClear,
  disabled,
  imgClassName = "h-24 max-w-xs rounded border border-brand-gray/60 bg-white object-cover",
  accept = "image/*",
}: AdminImagePickerProps) {
  const blobUrl = useFileObjectUrl(selectedFile);
  const remote = remoteUrl?.trim() ?? "";
  const preview = blobUrl || remote || null;

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <label htmlFor={inputId} className="block text-xs font-semibold text-brand-blue">
          {label}
        </label>
        {hint ? <div className="text-xs text-brand-blue/60">{hint}</div> : null}
      </div>
      {preview ? (
        <div className="flex flex-wrap items-end gap-3">
          <img src={preview} alt="" className={imgClassName} />
          <button
            type="button"
            onClick={onClear}
            disabled={disabled}
            className="shrink-0 text-xs font-bold uppercase tracking-wide text-red-700 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
          >
            Remove image
          </button>
        </div>
      ) : null}
      <input
        id={inputId}
        type="file"
        accept={accept}
        disabled={disabled}
        onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
        className={fileInputClass}
      />
    </div>
  );
}
